import { Activity, useEffect, useRef, useState, type ReactNode } from 'react';

import { Arriving, arriving, settled, useArrival, type Arrival } from './arrival';

/** Going in: 32 pixels of travel, which is a direction rather than a slide. */
const PUSH_MS = 260;
/**
 * Coming back, and it has to be the quicker of the two.
 *
 * The way out of a pushed screen is Telegram's own arrow, and an arrow that
 * has been tapped and is still waiting for the screen underneath reads as a
 * button that did not take. Leaving is always quicker than arriving here, the
 * same 220/180 rule every fold in the app already runs on.
 */
const POP_MS = 200;

/**
 * Which way a change of page goes, and how long the run is.
 *
 * Its own function because it is the whole of what this component decides and
 * the rest of it is DOM: a deeper page is arriving, a shallower one is being
 * come back to, and the same depth is neither.
 */
export function run(from: number, to: number): { back: boolean; ms: number } {
  const back = to < from;
  return { back, ms: back ? POP_MS : PUSH_MS };
}

/** A screen held in a slot: the one on screen, one leaving, or one kept under. */
type Held = { at: string; depth: number; node: ReactNode };
type Leaving = Held & { back: boolean; ms: number };
type Showing = {
  at: string;
  depth: number;
  /**
   * The screens a push covered, shallowest first, each still mounted and hidden
   * — C-023 (`FND-1618`). Coming back to one of them puts the caller's fresh
   * element into the slot it never left, so React keeps its state and its data
   * and nothing is drawn again from a skeleton.
   */
  kept: Held[];
  leaving: Leaving | null;
};

/**
 * One screen replacing another it was opened from, with the first still under it.
 *
 * Every drill-down in the app is a state change and nothing else - `page`,
 * `open`, `program` - so a list and the screen behind one of its rows swapped
 * places between two frames and the person was left to work out whether they
 * had gone somewhere or the list had been rewritten. Thirty-two pixels of
 * travel say «deeper», and only the screen nearer the finger travels them.
 *
 * **Two screens are never legible through each other.** Going in, an opaque lid
 * lies between the two slots for the length of the run and the screen arriving
 * does not fade at all; coming back there is no lid, because a lid would cover
 * exactly what the person has just come back to, and the screen leaving is the
 * only thing that moves. It used to fade both slots against each other, which
 * made the pair a double exposure the lid could not prevent - it *was* the
 * arriving screen's background, so it faded with it.
 *
 * **The screen that is leaving stays mounted.** It is held as the element it
 * was last rendered as and put in the slot it was already in, so React
 * reconciles it against itself and keeps its state: were it re-created from the
 * caller it would remount, ask the server again and slide out as a skeleton of
 * the screen the person is walking away from.
 *
 * **The screen that was covered stays mounted too** — C-023 (`FND-1618`), the
 * audit of 2026-10-27. Until then the slot under a pushed screen was emptied
 * when the run ended, so every «back» rebuilt the list from nothing: a skeleton,
 * the same requests again, and the blocks landing one by one — the schedule's
 * banner folding open again on every return, 68 pixels each time. Now the
 * covered screen sits in its slot inside an `Activity` that hides it: its DOM
 * stays, its state stays, its effects are taken down so no poll and no back-key
 * subscription runs from under another screen, and they are run again when it
 * is uncovered — which is the same set of requests a mount would have made,
 * issued at the same moment, so `screen_views` counts what it counted before
 * (`.claude/rules/contracts.md`). What the person sees on the way back is the
 * screen as they left it, and the fresh answer replaces it in place.
 *
 * Slots are keyed by the page's name rather than taking turns, because the key
 * is what tells React that the element arriving is the screen that was kept.
 *
 * Nothing runs for somebody who asked for less movement, and nothing runs while
 * the webview is off screen - Telegram minimises the app rather than closing
 * it, and a hidden document does not advance an animation, so a screen the
 * person had walked away from would sit under an unlifted lid until the app
 * came back.
 * Both are decided here rather than left to the stylesheet's own
 * `prefers-reduced-motion` block: the block shortens the animation, and what
 * has to go with it is the second slot. The covered screen is kept either way.
 *
 * Each slot also says, through `arrival.tsx`, until when it is still arriving.
 * That is the whole of «one arrival has one owner»: data that lands inside the
 * run is put on screen as it is, because the run it landed in is already the
 * entrance the finger asked for. A slot that is not running says so with a 0.
 */
export default function Push({
  at,
  /** How deep this page is. Arriving at a larger number is a push, at a smaller one a pop. */
  depth,
  motion = true,
  children,
}: {
  /** What is on screen. A change of it is what starts a run. */
  at: string;
  depth: number;
  /**
   * Whether this change of page is allowed to run. `false` for a change no finger
   * asked for — the running gathering opening itself on the launch answer (C-010,
   * C-023 §1 row 5) is a screen the person has not seen yet being replaced, and a
   * push away from it would be travel from nowhere.
   */
  motion?: boolean;
  children: ReactNode;
}) {
  const [showing, setShowing] = useState<Showing>(() => ({ at, depth, kept: [], leaving: null }));
  /**
   * What each slot is telling the screen inside it, and it is one object per
   * screen: replaced when a slot begins a run, kept untouched for the whole of
   * it, and for the whole time the screen is kept under another.
   *
   * The first slot inherits the arrival around this one rather than declaring
   * itself home. A `Push` mounted inside a tab that is still dissolving, or
   * inside a screen that is still riding in, is part of that arrival and not a
   * second one — which is what stopped a tab from fading its panel and then
   * fading the data on it a moment later.
   */
  const outer = useArrival();
  const arrivals = useRef<Map<string, Arrival>>(new Map([[at, outer ?? settled()]]));
  /**
   * The children as they were last rendered. Read at the moment the page
   * changes, which is the one moment the screen being left is still here to be
   * kept - by the next render the caller has already returned the new one.
   */
  const held = useRef<ReactNode>(children);

  // Worked out during the render that first sees the new page rather than in an
  // effect afterwards: an effect paints one frame with the arriving screen
  // already home and the leaving one gone, which is the jerk this exists to
  // remove, arriving from the other end.
  let now = showing;
  if (at !== showing.at) {
    const way = run(showing.depth, depth);
    const left: Held = { at: showing.at, depth: showing.depth, node: held.current };
    // Kept under the new screen: everything shallower than it, and the screen
    // being left when this is a push. A pop drops the deeper screen once it has
    // gone, and a change at the same depth is one screen giving way to another.
    const kept = showing.kept.filter((one) => one.depth < depth && one.at !== at);
    if (!way.back && showing.depth < depth) kept.push(left);
    const running = motion && !still();
    const going = running ? { ...left, ...way } : null;
    // Coming back to a screen that was kept, its arrival is the one it had: it
    // has nothing to arrive with, and a new object would re-render what reads it.
    if (!showing.kept.some((one) => one.at === at)) {
      // In this render and not in an effect after it: the slot is given the class
      // that starts the run here, and the screen inside has to be able to read
      // the end of that run in the very mount the class belongs to.
      arrivals.current.set(at, going === null ? settled() : arriving(going.ms));
    }
    now = { at, depth, kept, leaving: going };
    setShowing(now);
  }
  held.current = children;

  useEffect(() => {
    if (showing.leaving === null) return;
    const timer = window.setTimeout(
      () => setShowing((last) => (last.leaving === null ? last : { ...last, leaving: null })),
      showing.leaving.ms,
    );
    return () => window.clearTimeout(timer);
  }, [showing]);

  const keptNames = new Set(now.kept.map((one) => one.at));
  for (const name of arrivals.current.keys()) {
    if (name !== at && !keptNames.has(name) && name !== now.leaving?.at)
      arrivals.current.delete(name);
  }
  // A screen a push is leaving is kept, but for the length of the run it is the
  // leaving slot and not a hidden one: the same key may stand in one place only,
  // and when the run ends the slot stays where it is and is hidden in place.
  const hidden = now.kept.filter((one) => one.at !== now.leaving?.at);

  // One array for every slot, keyed by page. React matches keys inside one list
  // only: a slot written as a sibling of the list and later moved into it would
  // be a new element in a new place, and the screen inside it would start again
  // — which is exactly what a kept screen must not do.
  const slots: ReactNode[] = [
    <Activity key={at} mode="visible">
      <div className={slotClass(now)}>
        <Arriving arrival={arrivals.current.get(at) ?? settled()}>{children}</Arriving>
      </div>
    </Activity>,
  ];
  if (now.leaving !== null) {
    slots.push(
      <Activity key={now.leaving.at} mode="visible">
        <div
          className={
            now.leaving.back
              ? 'push-slot push-slot--leaving push-slot--back-out'
              : 'push-slot push-slot--leaving push-slot--out'
          }
        >
          <Arriving arrival={arrivals.current.get(now.leaving.at) ?? settled()}>
            {now.leaving.node}
          </Arriving>
        </div>
      </Activity>,
    );
  }
  for (const one of hidden) {
    slots.push(
      <Activity key={one.at} mode="hidden">
        <div className="push-slot">
          <Arriving arrival={arrivals.current.get(one.at) ?? settled()}>{one.node}</Arriving>
        </div>
      </Activity>,
    );
  }

  return (
    <div className={now.leaving === null ? 'push' : 'push push--running'}>
      {/* Every slot is keyed by its page, and a kept one is hidden rather than
          gone. That is what makes the slot a screen comes back to the same one
          it was left in, and so the difference between a screen that keeps its
          data on the way back and one that is drawn again from nothing. The
          leaving screen is the one slot not kept: a pop drops it with the run.
          The screen on view stands first in the DOM, the hidden ones last — the
          stack decides what is painted over what, and a reader of the tree
          (a test, a screen reader) meets the screen that is on view first. */}
      {slots}
      {/* The lid, and only going in. Its own element rather than the arriving
          slot's background, because that background was animated along with the
          screen that painted it and a half-faded lid is not a lid. Coming back
          there is nothing to hide: the screen returned to is whole and still. */}
      {now.leaving !== null && !now.leaving.back && <div className="push-lid" />}
    </div>
  );
}

/** The class of the slot on screen: arriving, being come back to, or standing. */
function slotClass(now: Showing): string {
  if (now.leaving === null) return 'push-slot';
  return now.leaving.back ? 'push-slot push-slot--back-in' : 'push-slot push-slot--in';
}

/** Whether this is a moment to change nothing but what is on screen. */
function still(): boolean {
  return (
    typeof window === 'undefined' ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    document.visibilityState !== 'visible'
  );
}
